// src/pages/PrivacyPolicy.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useBranding } from '../context/BrandingContext';
import { useRegionalSettings } from '../context/RegionalSettingsContext';
import { EU_PAYMENT_COUNTRY_CODES } from '../config/countryProfiles';

function Section({ title, children }) {
    return (
        <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <div className="text-sm text-gray-700 space-y-2 leading-relaxed">{children}</div>
        </section>
    );
}

function StrongTrans({ i18nKey, values }) {
    return <Trans i18nKey={i18nKey} values={values} components={{ strong: <strong /> }} />;
}

export default function PrivacyPolicy() {
    const { t } = useTranslation();
    const { appName } = useBranding();
    const { settings } = useRegionalSettings();
    const name = appName || 'Attendance App';
    const cc = String(settings?.countryCode || '').toUpperCase();
    const isGdpr = EU_PAYMENT_COUNTRY_CODES.has(cc) || cc === 'GB';

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4">
            <div className="mx-auto max-w-3xl bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-10 space-y-8">
                <div className="space-y-1">
                    <Link to="/login" className="text-xs text-blue-600 hover:underline">{t('legal.backToLogin')}</Link>
                    <h1 className="text-2xl font-bold text-gray-900 mt-2">{t('legal.privacyTitle')}</h1>
                    <p className="text-xs text-gray-400">
                        {t('privacy.metaLastUpdated', { name })}
                        {isGdpr ? t('privacy.metaGdprFraming') : t('privacy.metaDpdpFraming')}
                    </p>
                </div>

                <Section title={t('privacy.whoWeAre.title')}>
                    <p><StrongTrans i18nKey="privacy.whoWeAre.p1" values={{ name }} /></p>
                    <p>{t('privacy.whoWeAre.p2')}</p>
                </Section>

                <Section title={t('privacy.whatWeCollect.title')}>
                    <p>{t('privacy.whatWeCollect.intro')}</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><StrongTrans i18nKey="privacy.whatWeCollect.identity" /></li>
                        <li><StrongTrans i18nKey="privacy.whatWeCollect.contact" /></li>
                        <li><StrongTrans i18nKey="privacy.whatWeCollect.account" /></li>
                        <li><StrongTrans i18nKey="privacy.whatWeCollect.attendance" /></li>
                        <li><StrongTrans i18nKey="privacy.whatWeCollect.financial" /></li>
                        <li><StrongTrans i18nKey="privacy.whatWeCollect.guardian" /></li>
                        <li><StrongTrans i18nKey="privacy.whatWeCollect.technical" /></li>
                    </ul>
                    <p><StrongTrans i18nKey="privacy.whatWeCollect.noBiometric" /></p>
                    <p><StrongTrans i18nKey="privacy.whatWeCollect.payments" /></p>
                </Section>

                <Section title={t('privacy.legalBasis.title')}>
                    {isGdpr ? (
                        <ul className="list-disc pl-5 space-y-1">
                            <li><StrongTrans i18nKey="privacy.legalBasis.gdpr.contract" /></li>
                            <li><StrongTrans i18nKey="privacy.legalBasis.gdpr.legitimate" /></li>
                            <li><StrongTrans i18nKey="privacy.legalBasis.gdpr.obligation" /></li>
                            <li><StrongTrans i18nKey="privacy.legalBasis.gdpr.consent" /></li>
                        </ul>
                    ) : (
                        <ul className="list-disc pl-5 space-y-1">
                            <li><StrongTrans i18nKey="privacy.legalBasis.dpdp.contract" /></li>
                            <li><StrongTrans i18nKey="privacy.legalBasis.dpdp.legitimate" /></li>
                            <li><StrongTrans i18nKey="privacy.legalBasis.dpdp.obligation" /></li>
                        </ul>
                    )}
                </Section>

                <Section title={t('privacy.retention.title')}>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><StrongTrans i18nKey="privacy.retention.activeAccount" /></li>
                        <li><StrongTrans i18nKey="privacy.retention.attendance" /></li>
                        <li>
                            <StrongTrans
                                i18nKey={isGdpr ? 'privacy.retention.financialGdpr' : 'privacy.retention.financialDpdp'}
                            />
                        </li>
                        <li><StrongTrans i18nKey="privacy.retention.auditLogs" /></li>
                    </ul>
                </Section>

                <Section title={t('privacy.sharing.title')}>
                    <p>{t('privacy.sharing.intro')}</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><StrongTrans i18nKey="privacy.sharing.gateways" /></li>
                        <li><StrongTrans i18nKey="privacy.sharing.messaging" /></li>
                        <li><StrongTrans i18nKey="privacy.sharing.hosting" /></li>
                        <li><StrongTrans i18nKey="privacy.sharing.licence" /></li>
                        <li><StrongTrans i18nKey="privacy.sharing.staff" /></li>
                    </ul>
                </Section>

                <Section title={t('privacy.cookies.title')}>
                    <p>{t('privacy.cookies.intro')}</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><StrongTrans i18nKey="privacy.cookies.jwt" /></li>
                        <li><StrongTrans i18nKey="privacy.cookies.consentFlag" /></li>
                        <li><StrongTrans i18nKey="privacy.cookies.gatewaySession" /></li>
                    </ul>
                    <p>{t('privacy.cookies.noAds')}</p>
                </Section>

                {isGdpr ? (
                    <Section title={t('privacy.rights.gdprTitle')}>
                        <p>{t('privacy.rights.gdprP1')}</p>
                        <p>{t('privacy.rights.gdprP2')}</p>
                        <p>{t('privacy.rights.gdprP3')}</p>
                    </Section>
                ) : (
                    <Section title={t('privacy.rights.dpdpTitle')}>
                        <p>{t('privacy.rights.dpdpP1')}</p>
                        <p>{t('privacy.rights.dpdpP2')}</p>
                    </Section>
                )}

                <Section title={t('privacy.security.title')}>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>{t('privacy.security.https')}</li>
                        <li>{t('privacy.security.bcrypt')}</li>
                        <li>{t('privacy.security.roles')}</li>
                        <li>
                            <Trans
                                i18nKey="privacy.security.encryption"
                                components={{ code: <code className="text-xs" /> }}
                            />
                        </li>
                    </ul>
                </Section>

                <Section title={t('privacy.changes.title')}>
                    <p>{t('privacy.changes.body')}</p>
                </Section>

                <div className="border-t border-gray-100 pt-6 text-xs text-gray-400 space-y-1">
                    <p>{t('privacy.footer.line1', { name })}</p>
                    <p>
                        <Trans
                            i18nKey="privacy.footer.line2"
                            components={{
                                code: <code className="text-xs" />,
                                codeEu: <code className="text-xs" />,
                            }}
                        />
                    </p>
                </div>
            </div>
        </div>
    );
}
